/**
 * Formats a number of parameters into a human-readable string
 * @param params - The number of parameters
 * @returns Formatted string (e.g., "6.7B", "1.2M", "500K")
 */
export const formatParameters = (params: number): string => {
    const formatNumber = (num: number): string => {
        return num % 1 === 0 ? num.toString() : num.toFixed(1);
    };

    if (params >= 1000000000000) {
        return `${formatNumber(params / 1000000000000)}T`;
    } else if (params >= 1000000000) {
        return `${formatNumber(params / 1000000000)}B`;
    } else if (params >= 1000000) {
        return `${formatNumber(params / 1000000)}M`;
    } else if (params >= 1000) {
        return `${formatNumber(params / 1000)}K`;
    }
    return params.toString();
};

/**
 * Capitalizes the first letter of a string and makes the rest lowercase
 * @param str - The string to capitalize
 * @returns Capitalized string
 */
export const capitalizeFirst = (str: string): string => {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Formats model information into a display string
 * @param modelType - The type of model (e.g., "text")
 * @param size - The size of the model (e.g., "small")
 * @param parameters - The number of parameters
 * @returns Formatted string (e.g., "Text | Small | 6.7B Parameters")
 */
export const formatModelInfo = (
    modelType?: string,
    size?: string,
    parameters?: number
): string => {
    const info = [
        modelType ? capitalizeFirst(modelType) : '',
        size ? capitalizeFirst(size) : '',
        parameters ? `${formatParameters(parameters)} Parameters` : ''
    ].filter(Boolean);

    return info.join(' | ');
};

/**
 * Generates deployment code snippet based on model type
 * @param modelType - The type of model (text, vision, embedding)
 * @param modelShortString - The short string identifier for the model
 * @returns Python deployment code snippet
 */
export const generateDeploymentCode = (modelType: string, modelShortString: string): string => {
    return `from predibase import Predibase, DeploymentConfig

# Initialize the client with your API token
pb = Predibase(api_token="<PREDIBASE API TOKEN>")

# Create a deployment
deployment = pb.deployments.create(
    name="my-${modelShortString}",
    config=DeploymentConfig(base_model="${modelShortString}")
)`;
};

/**
 * Generates the deploy URL for redirecting to the Predibase app
 * @param modelShortString - The short string identifier for the model
 * @returns The full deploy URL
 */
export const generateDeployUrl = (modelShortString: string): string => {
    const predibaseAppEnvironment = process.env.GATSBY_PREDIBASE_APP_ENVIRONMENT;
    const baseUrl = `https://${predibaseAppEnvironment}`;
    return `${baseUrl}/deployments/create?model=${modelShortString}`;
};

/**
 * Handles the deploy button click by redirecting to the appropriate deploy URL
 * @param modelShortString - The short string identifier for the model
 */
export const handleDeploy = (modelShortString: string): void => {
    const deployUrl = generateDeployUrl(modelShortString);
    window.open(deployUrl, '_blank');
};

/**
 * Gets the deploy URL for a given model
 * @param modelShortString - The short string identifier for the model
 * @returns The full deploy URL
 */
export const getDeployUrl = (modelShortString: string): string => {
    return generateDeployUrl(modelShortString);
};

/**
 * Generates Python inference code snippet
 * @param modelType - The type of model (text, vision, embedding)
 * @param modelShortString - The short string identifier for the model
 * @param inferenceType - The inference type (base, adapter, hf-adapter)
 * @returns Python inference code snippet
 */
export const generatePythonInferenceCode = (
    modelType: string,
    modelShortString: string,
    inferenceType: string = 'base'
): string => {
    if (modelType === 'text') {
        return `from predibase import Predibase

# Initialize client
pb = Predibase(api_token="<PREDIBASE_API_TOKEN>")

# Get deployment client
client = pb.deployments.client("my-${modelShortString}")  # Private deployment name, or use a shared endpoint like "${modelShortString}"

# Generate text
response = client.generate(
    "What is machine learning?",
    max_new_tokens=100,
    temperature=0.7
)
print(response.generated_text)

# Stream responses
for response in client.generate_stream(
    "Write a story about a robot learning to paint.",
    max_new_tokens=200
):
    print(response.token.text, end="", flush=True)`;
    }

    // Placeholder for vision and embedding types
    return `# ${modelType} inference code coming soon...`;
};

/**
 * Generates curl inference code snippet
 * @param modelType - The type of model (text, vision, embedding)
 * @param modelShortString - The short string identifier for the model
 * @param inferenceType - The inference type (base, adapter, hf-adapter)
 * @returns Curl inference code snippet
 */
export const generateCurlInferenceCode = (
    modelType: string,
    modelShortString: string,
    inferenceType: string = 'base'
): string => {
    if (modelType === 'text') {
        return `# Set your credentials
export PREDIBASE_API_TOKEN="<YOUR TOKEN>"
export PREDIBASE_ENDPOINT="https://serving.app.predibase.com/<TENANT_ID>/deployments/v2/llms/<DEPLOYMENT NAME>"

# Chat completion
curl -i $PREDIBASE_ENDPOINT/v1/chat/completions \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer $PREDIBASE_API_TOKEN" \\
  -d '{
    "model": "",
    "messages": [
      {"role": "user", "content": "What is machine learning?"}
    ],
    "max_tokens": 100
  }'`;
    }

    // Placeholder for vision and embedding types
    return `# ${modelType} curl inference code coming soon...`;
}; 